import { faClose, faSliders } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Modal } from '@orobix/orobix-fe-components'
import { isEmpty, isNil, keys, map, pipe, reject, values, zip } from 'ramda'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useStyles from './FiltersModal.styles'

type FiltersModalProps<T> = {
  initialValues?: Partial<T>
  labels?: { [key in keyof T]: string }
  children: ({
    filters,
    setFilters,
  }: {
    filters: Partial<T>
    setFilters: (
      stateTransitionFn: (prevFilters: Partial<T>) => Partial<T>
    ) => void
  }) => React.ReactNode
  removeDisabled: boolean
  onSave: (filters: Partial<T>) => void
  onRemove: () => void
  disabled?: boolean
}

const FiltersModal = <Filters,>({
  initialValues,
  labels,
  children,
  removeDisabled,
  onSave,
  onRemove,
  disabled = false,
}: FiltersModalProps<Filters>) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const [isOpen, setOpen] = useState(false)
  const [filters, setFilters] = useState<Partial<Filters>>(initialValues || {})
  const [lastFilters, setLastFilters] = useState<Partial<Filters>>(
    initialValues || {}
  )

  /**
   * Returns an array of keys in `Filters` such that their associated values
   * are not undefined.
   * Excludes 'datasetExclude' as it's a modifier, not a standalone filter.
   */
  const getActiveFiltersKeys: (
    fs: Partial<Filters>
  ) => (keyof Filters)[] = pipe(
    (x: Partial<Filters>) => zip(keys(x), values(x)),
    reject(
      ([key, value]) =>
        isNil(value) || isEmpty(value) || key === 'datasetExclude'
    ),
    map(([key, _]) => key)
  )

  useEffect(() => {
    setFilters(initialValues || {})
    setLastFilters(initialValues || {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  const activeFiltersKeys = getActiveFiltersKeys(lastFilters)
  const numberOfActiveFilters = activeFiltersKeys.length

  const activeFiltersLabels = labels
    ? activeFiltersKeys.map((key) => labels[key as keyof Filters].toLowerCase())
    : []

  const activeFiltersLabelsStr = activeFiltersLabels.length
    ? `: ${activeFiltersLabels.join(', ')}`
    : ''

  const activeFiltersCounterStr = numberOfActiveFilters
    ? ` (${numberOfActiveFilters})`
    : ''

  return (
    <>
      <Button
        label={t('Remove filters')}
        type="blank-default"
        icon={<FontAwesomeIcon icon={faClose} />}
        disabled={disabled || (removeDisabled && numberOfActiveFilters === 0)}
        onClick={() => {
          setFilters(initialValues || {})
          setLastFilters(initialValues || {})
          onRemove()
        }}
      />
      <Button
        onClick={() => setOpen(true)}
        label={`${t('Filters')}${activeFiltersCounterStr}`}
        icon={<FontAwesomeIcon icon={faSliders} />}
        title={
          numberOfActiveFilters
            ? `${t('Active filters')}${activeFiltersLabelsStr}`
            : t('No active filters')
        }
        buttonType="button"
        type="blank-default"
        disabled={disabled}
      />
      {isOpen && (
        <Modal
          size="sm"
          onCloseRequest={() => {
            setOpen(false)
            setFilters(lastFilters)
          }}
          title={t('Filters')}
          footer={
            <Button
              onClick={() => {
                setOpen(false)
                setLastFilters(filters)
                onSave(filters)
              }}
              label={t('Set filters')}
              type="action"
              buttonType="button"
            />
          }
        >
          <div className={classes.container}>
            <p className={classes.hint}>
              {t('Filter values can be partially specified')}
            </p>
            {children({ filters, setFilters })}
          </div>
        </Modal>
      )}
    </>
  )
}

export default FiltersModal
